import type { ErrorCode } from '@orienta/shared';

export const API_BASE = '/api/v1';

/** Erro padronizado da API: { error: { code, message, details?, requestId } }. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Erros por campo (details.fields) enviados pela validação do servidor. */
  get fields(): Record<string, string> {
    const d = this.details as { fields?: Record<string, string> } | undefined;
    return d?.fields ?? {};
  }
}

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}
export function getCsrfToken() {
  return csrfToken;
}
export function onUnauthorized(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Não redireciona para o login em 401 (ex.: verificação da sessão). */
  skipAuthRedirect?: boolean;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (MUTATING.has(method) && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', 'Não foi possível conectar ao servidor. Verifique sua conexão.');
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const e = (data as { error?: { code?: ErrorCode; message?: string; details?: unknown; requestId?: string } } | undefined)?.error;
    const err = new ApiError(
      res.status,
      e?.code ?? 'INTERNAL_ERROR',
      e?.message ?? 'Erro inesperado. Tente novamente.',
      e?.details,
      e?.requestId ?? res.headers.get('x-request-id') ?? undefined,
    );
    if (res.status === 401 && !opts.skipAuthRedirect) unauthorizedHandler?.();
    throw err;
  }
  return data as T;
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) => api<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body: body ?? {} }),
  del: <T>(path: string, body?: unknown) => api<T>(path, { method: 'DELETE', body }),
};

/** Aplica erros de campo da API a um formulário do React Hook Form. Retorna true se aplicou algum. */
export function applyFieldErrors(
  err: unknown,
  setError: (name: never, error: { type: string; message: string }) => void,
): boolean {
  if (!(err instanceof ApiError)) return false;
  const entries = Object.entries(err.fields);
  for (const [name, message] of entries) setError(name as never, { type: 'server', message });
  return entries.length > 0;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.requestId && err.status >= 500 ? `${err.message} (código: ${err.requestId})` : err.message;
  if (err instanceof Error) return err.message;
  return 'Erro inesperado.';
}
